import { ArchiveAttachments } from '../components/ArchiveAttachments';
import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { can, computeTotal, type ArchiveEntryInput, WINE_LETTERS } from '@gwc/shared';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { membersApi } from '../api/members';
import { ScoreSegment } from '../components/ScoreSegment';

type Entry = { id: string; payload: ArchiveEntryInput; status: string; reviewNotes: string | null };
export function ArchiveEntry() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fresh = (): ArchiveEntryInput => ({
    title: '',
    eventDate: '',
    personId: user?.id ?? '',
    wine: { letter: 'A', wineName: '' },
    score: { sightNose: null, taste: null, overall: null, notes: '' },
    attachments: [],
  });
  const [form, setForm] = useState(fresh);
  const [editing, setEditing] = useState<string | null>(null);
  const clientId = useRef(crypto.randomUUID());
  const [attachmentType, setAttachmentType] = useState<'menu' | 'label' | 'scorecard' | 'photo'>(
    'menu',
  );
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const entries = useQuery({
    queryKey: ['archive-entries', page],
    queryFn: () => api.get<{ entries: Entry[]; total: number }>(`/archive-entries?page=${page}`),
  });
  const members = useQuery({ queryKey: ['members'], queryFn: () => membersApi.list() });
  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch(`/archive-entries/${editing}`, form)
        : api.post('/archive-entries', { ...form, clientId: clientId.current }),
    onSuccess: () => {
      setNotice('Entry saved for administrator review.');
      setForm(fresh());
      setEditing(null);
      clientId.current = crypto.randomUUID();
      void qc.invalidateQueries({ queryKey: ['archive-entries'] });
    },
  });
  const review = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.post(`/archive-entries/${id}/review`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['archive-entries'] }),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('type', attachmentType);
      body.append('file', file);
      const res = await api.upload<{ mediaId: string; url: string }>('/media', body);
      setForm((f) => ({
        ...f,
        attachments: [...f.attachments, { mediaId: res.mediaId, type: attachmentType }],
        ...(attachmentType === 'label' ? { labelImageUrl: res.url } : {}),
      }));
    },
  });
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <h1 className="font-display text-3xl">Paper archive entry</h1>
      <p>
        Enter one attendee’s scorecard for one wine. Entries from the same tasting date and title
        are grouped after review.
      </p>
      <form
        className="card space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label className="block">
          Tasting title
          <input
            required
            className="field"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </label>
        <label className="block">
          Tasting date
          <input
            type="date"
            required
            className="field"
            value={form.eventDate}
            onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
          />
        </label>
        <label className="block">
          Whose scorecard?
          <select
            required
            className="field"
            value={form.personId}
            onChange={(e) => setForm({ ...form, personId: e.target.value })}
          >
            <option value="">Choose attendee</option>
            {members.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.firstName} {p.lastName}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          Wine letter
          <select
            className="field"
            value={form.wine.letter}
            onChange={(e) =>
              setForm({
                ...form,
                wine: { ...form.wine, letter: e.target.value as typeof form.wine.letter },
              })
            }
          >
            {WINE_LETTERS.map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
        </label>
        {(['wineName', 'producer', 'regionAva', 'vineyard', 'varietal'] as const).map((key) => (
          <label className="block" key={key}>
            {
              {
                wineName: 'Wine name',
                producer: 'Producer',
                regionAva: 'Region / Commune',
                vineyard: 'Vineyard',
                varietal: 'Varietal / Type',
              }[key]
            }
            <input
              className="field"
              required={key === 'wineName'}
              value={form.wine[key] ?? ''}
              onChange={(e) => setForm({ ...form, wine: { ...form.wine, [key]: e.target.value } })}
            />
          </label>
        ))}
        <label className="block">
          Vintage
          <input
            className="field"
            type="number"
            min={1800}
            max={2100}
            value={form.wine.vintage ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                wine: { ...form.wine, vintage: e.target.value ? Number(e.target.value) : null },
              })
            }
          />
        </label>
        {(['sightNose', 'taste', 'overall'] as const).map((key) => (
          <div key={key}>
            <p>{{ sightNose: 'Sight / Nose', taste: 'Taste', overall: 'Overall' }[key]}</p>
            <ScoreSegment
              label={key}
              max={key === 'overall' ? 4 : 3}
              value={form.score[key]}
              onPick={(value) => setForm({ ...form, score: { ...form.score, [key]: value } })}
            />
          </div>
        ))}
        <p>Total: {computeTotal(form.score) ?? '—'} / 10 · Hold a number for quarter points.</p>
        <label className="block">
          Personal tasting notes
          <textarea
            className="field"
            maxLength={2000}
            value={form.score.notes ?? ''}
            onChange={(e) => setForm({ ...form, score: { ...form.score, notes: e.target.value } })}
          />
        </label>
        <div>
          <label>
            Photo / scan type
            <select
              className="field"
              value={attachmentType}
              onChange={(e) => setAttachmentType(e.target.value as typeof attachmentType)}
            >
              {['menu', 'label', 'scorecard', 'photo'].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          <input
            aria-label="Upload photo or scan"
            type="file"
            accept="image/*"
            disabled={upload.isPending}
            onChange={(e) => {
              if (e.target.files?.[0]) upload.mutate(e.target.files[0]);
              e.target.value = '';
            }}
          />
          <p>{form.attachments.length} attachments</p>
        </div>
        <button className="btn-primary" disabled={save.isPending || upload.isPending}>
          {editing ? 'Save corrected entry' : 'Submit for review'}
        </button>
      </form>
      {notice && <p role="status">{notice}</p>}
      {(save.error || upload.error || review.error) && (
        <p role="alert">{(save.error || upload.error || review.error)?.message}</p>
      )}
      {editing && <ArchiveAttachments entryId={editing} />}
      <h2 className="font-display text-2xl">Submitted entries</h2>
      {entries.data?.entries.map((entry) => (
        <div className="card space-y-2" key={entry.id}>
          <h3>
            {entry.payload.title} · {entry.payload.eventDate} · {entry.payload.wine.letter}:{' '}
            {entry.payload.wine.wineName}
          </h3>
          <p>
            {entry.status} · Total {computeTotal(entry.payload.score) ?? '—'}
          </p>
          <p>{entry.payload.score.notes}</p>
          {entry.reviewNotes && <p>{entry.reviewNotes}</p>}
          {(entry.status !== 'approved' || can(user?.roles ?? [], 'records:approve')) && (
            <div className="flex gap-2">
              <button
                className="btn-ghost"
                onClick={() => {
                  setEditing(entry.id);
                  setForm(entry.payload);
                  window.scrollTo(0, 0);
                }}
              >
                Review / edit
              </button>
              {entry.status !== 'approved' && can(user?.roles ?? [], 'records:approve') && (
                <>
                  <button
                    className="btn-primary"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: entry.id, status: 'approved' })}
                  >
                    Approve
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: entry.id, status: 'rejected' })}
                  >
                    Reject
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      <div className="flex gap-3">
        <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Previous entries
        </button>
        <span>Page {page}</span>
        <button
          className="btn-ghost"
          disabled={page * 25 >= (entries.data?.total ?? 0)}
          onClick={() => setPage((p) => p + 1)}
        >
          Next entries
        </button>
      </div>
    </div>
  );
}
